import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import HeroRotunda from '@/components/ui/HeroRotunda'
import SectionHeader from '@/components/ui/SectionHeader'
import ArchiveGrid from '@/components/ui/ArchiveGrid'
import LatestGrid from '@/components/ui/LatestGrid'
import ThemeRail from '@/components/ui/ThemeRail'
import PatreonPanel from '@/components/ui/PatreonPanel'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import JsonLd, { organizationSchema, websiteSchema } from '@/components/seo/JsonLd'
import { useSEO } from '@/hooks/useSEO'
import type { Archive, Theme, LatestContent } from '@/types'
import * as api from '@/lib/api'
import { SmartImage } from '../components/ui/SmartImage'

export default function HomePage() {
  // SEO
  useSEO({
    title: 'The unified gateway to humanity\'s memory',
    description: 'The Archive Network — a growing network of interconnected archives across civilizations, conflicts, treaties, disasters, philosophy and more, explored through chapters, podcasts, and documentary video.',
    url: 'https://thearchivenetwork.com/',
  })

  const [loading, setLoading] = useState(true)
  const [archives, setArchives] = useState<Archive[]>([])
  const [latest, setLatest] = useState<LatestContent>({})
  const [themes, setThemes] = useState<Theme[]>([])
  
  useEffect(() => {
    async function loadData() {
      try {
        const [archivesData, latestData, themesData] = await Promise.all([
          api.getArchives(),
          api.getLatest(),
          api.getThemes(),
        ])
        setArchives(archivesData)
        setLatest(latestData)
        setThemes(themesData)
      } catch (error) {
        console.error('Failed to load homepage data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Structured Data */}
      <JsonLd schema={[organizationSchema, websiteSchema]} />

      {/* Hero Rotunda */}
      <HeroRotunda />

      {/* Speak with History — flagship product (external). Imageless band by design. */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <a
            href="https://speak.thearchivenetwork.com"
            target="_blank"
            rel="noopener noreferrer"
            className="group block overflow-hidden rounded-2xl border border-archive-gold/30 bg-archive-charcoal/50 p-8 sm:p-12 transition-colors hover:border-archive-gold/60"
          >
            <div className="flex flex-col items-center text-center sm:flex-row sm:items-center sm:gap-8 sm:text-left">
              <svg className="w-16 h-16 shrink-0 text-archive-gold/70 group-hover:text-archive-gold transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M21 12a8 8 0 01-11.6 7.13L4 20l1.13-5.4A8 8 0 1121 12z" />
              </svg>
              <div className="mt-4 sm:mt-0">
                {/* NOTE: "New ·" is a launch-temporary label — remove once SwH is no longer new. */}
                <span className="inline-block mb-2 text-xs uppercase tracking-wider text-archive-gold">New · Speak with History</span>
                <h2 className="font-display text-2xl sm:text-3xl text-archive-cream group-hover:text-archive-gold transition-colors">
                  Have a conversation with history
                </h2>
                <p className="mt-2 max-w-2xl text-archive-muted">
                  Speak with the figures of history — from Caesar to Cleopatra — each in their own voice.
                </p>
                <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-archive-gold/30 bg-archive-gold/10 px-5 py-2.5 text-sm uppercase tracking-wider text-archive-gold">
                  Begin a Conversation →
                </span>
              </div>
            </div>
          </a>
        </div>
      </section>

      {/* Choose Your Archive */}
      <section className="py-16 sm:py-24 bg-archive-charcoal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            title="Choose Your Archive"
            subtitle="A growing network of interconnected archives preserving humanity's most significant moments, movements, and memories."
            centered
          />
          <ArchiveGrid archives={archives} />
        </div>
      </section>

      {/* Guided by the Curators */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/curators"
            className="group block overflow-hidden rounded-2xl border border-archive-gold/30 bg-archive-charcoal/50 p-8 sm:p-12 transition-colors hover:border-archive-gold/60"
          >
            <div className="flex flex-col items-center gap-8 text-center sm:flex-row sm:text-left">
              {/* Curator portraits */}
              <div className="flex shrink-0 items-end justify-center">
                <SmartImage
                  src="/images/curators/aurelius.png"
                  alt="Aurelius"
                  sizes="216px"
                  width={216}
                  height={144}
                  loading="lazy"
                  className="h-28 sm:h-36 w-auto object-contain drop-shadow-xl -mr-3"
                />
                <SmartImage
                  src="/images/curators/malik.png"
                  alt="Malik"
                  sizes="107px"
                  width={107}
                  height={160}
                  loading="lazy"
                  className="h-32 sm:h-40 w-auto object-contain drop-shadow-xl"
                />
              </div>
              <div>
                <span className="inline-block mb-2 text-xs uppercase tracking-wider text-archive-gold">The Curators</span>
                <h2 className="font-display text-2xl sm:text-3xl text-archive-cream group-hover:text-archive-gold transition-colors">
                  Guided by the Curators
                </h2>
                <p className="mt-3 max-w-2xl text-archive-muted">
                  The Archive is kept by three: Marcus, who tells its stories; and Aurelius and Malik, who
                  debate what they mean. Two scholars a thousand years apart, reading the same history
                  through different eyes.
                </p>
                <span className="mt-5 inline-flex items-center gap-2 rounded-full border border-archive-gold/30 bg-archive-gold/10 px-5 py-2.5 text-sm uppercase tracking-wider text-archive-gold">
                  Meet the Curators →
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Latest From The Network */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            title="Latest From The Network"
            subtitle="Recent episodes, conversations, and artifacts from across the archives."
          />
          <LatestGrid
            podcast={latest.podcast}
          />
        </div>
      </section>

      {/* Explore by Themes */}
      <section className="py-16 sm:py-24 bg-archive-charcoal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            title="Explore by Theme"
            subtitle="Cross-archive themes connecting treaties, conflicts, civilizations, and more."
            ctaText="View all themes"
            ctaLink="/themes"
          />
          <ThemeRail themes={themes.slice(0, 8)} />
        </div>
      </section>

      {/* Artifacts (Shop Teaser) - Coming Soon */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            title="Archive Artifacts"
            subtitle="Curated prints, medallions, and seals from the archives. Museum-quality collectibles."
            centered
          />
          <div className="relative rounded-2xl border border-archive-ash/30 bg-archive-charcoal/50 p-12 text-center">
            <div className="flex flex-col items-center justify-center">
              <svg className="w-16 h-16 text-archive-gold/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h3 className="mt-4 font-display text-xl text-archive-cream">Archive Shop</h3>
              <p className="mt-2 text-archive-muted max-w-md">Premium prints, medallions, and collectibles from across the archives.</p>
              <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-archive-gold/30 bg-archive-gold/10 px-5 py-2.5">
                <div className="h-2 w-2 animate-pulse rounded-full bg-archive-gold" />
                <span className="text-sm uppercase tracking-wider text-archive-gold">Coming Soon</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Patreon Panel */}
      <PatreonPanel />
    </div>
  )
}
